import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

// Global state to track active tooltips
let activeTooltipId: string | null = null;

interface FeatureTooltipProps {
  id: string; // Unique ID for the tooltip (used for localStorage)
  content: string; // The tooltip content
  position?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number; // Delay before showing in ms
  children: React.ReactNode; // The element to attach the tooltip to
  className?: string;
  offset?: number; // Custom offset value for positioning
  align?: 'left' | 'center' | 'right'; // How to align the tooltip
}

export const FeatureTooltip = ({ 
  id, 
  content, 
  position = 'top', 
  delay = 500,
  children,
  className,
  offset = 0,
  align = 'center'
}: FeatureTooltipProps) => {
  const [visible, setVisible] = useState(false);
  const [hasSeenTooltip, setHasSeenTooltip] = useState(false);
  const isMobile = useIsMobile();
  
  // On mobile, move tooltips to bottom for better visibility
  const mobilePosition = isMobile ? 'bottom' : position;
  
  useEffect(() => {
    // Check if user has already seen this specific tooltip
    const tooltipSeen = localStorage.getItem(`strykr-tooltip-${id}`);
    
    if (!tooltipSeen) {
      const timer = setTimeout(() => {
        // Only show this tooltip if no other tooltip is currently active
        if (!activeTooltipId || activeTooltipId === id) {
          activeTooltipId = id;
          setVisible(true);
        }
      }, delay);
      return () => clearTimeout(timer);
    } else {
      setHasSeenTooltip(true);
    }
  }, [id, delay]);
  
  const dismissTooltip = (e: React.MouseEvent) => {
    // Prevent the click from passing through to elements behind
    e.stopPropagation();
    e.preventDefault();
    
    setVisible(false);
    localStorage.setItem(`strykr-tooltip-${id}`, 'true');
    setHasSeenTooltip(true);
    
    // Clear the active tooltip ID
    if (activeTooltipId === id) {
      activeTooltipId = null;
    }
  };
  
  // When this component unmounts, clear the active tooltip if it's this one
  useEffect(() => {
    return () => {
      if (activeTooltipId === id) {
        activeTooltipId = null;
      }
    };
  }, [id]);
  
  return (
    <div className="relative inline-flex">
      {children}
      
      {visible && !hasSeenTooltip && (
        <div 
          className={cn(
            "absolute z-[100] pointer-events-auto",
            "bg-gradient-to-br from-indigo-900/95 to-blue-900/95",
            "border border-indigo-500/30 rounded-lg p-3 shadow-lg",
            "animate-fadeIn",
            isMobile ? "min-w-[200px] max-w-[260px]" : "min-w-[260px] max-w-[320px]",
            "w-full",
            // Positioning classes
            mobilePosition === 'top' && 'bottom-full mb-2',
            mobilePosition === 'bottom' && 'top-full mt-2',
            mobilePosition === 'left' && 'right-full mr-2 top-1/2 -translate-y-1/2',
            mobilePosition === 'right' && 'left-full ml-2 top-1/2 -translate-y-1/2',
            // Alignment classes - only apply if no custom className is provided
            !className && align === 'center' && 'left-1/2 -translate-x-1/2',
            !className && align === 'left' && 'left-0',
            !className && align === 'right' && 'right-0',
            className
          )}
          style={{ 
            marginBottom: mobilePosition === 'top' ? '12px' : undefined,
            marginTop: mobilePosition === 'bottom' ? '12px' : undefined
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-start mb-1">
            <div className={cn("text-white", isMobile ? "text-xs" : "text-sm")}>{content}</div>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-6 w-6 -mt-1 -mr-1 text-white/70 hover:text-white hover:bg-blue-800/30"
              onClick={dismissTooltip}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Arrow */}
          <div 
            className={cn(
              "absolute h-2 w-2 bg-indigo-900 rotate-45 border border-indigo-500/30",
              mobilePosition === 'top' && 'top-full -mt-1 border-t-0 border-l-0',
              mobilePosition === 'bottom' && 'bottom-full -mb-1 border-b-0 border-r-0',
              mobilePosition === 'left' && 'left-full -ml-1 top-1/2 -translate-y-1/2 border-b-0 border-l-0',
              mobilePosition === 'right' && 'right-full -mr-1 top-1/2 -translate-y-1/2 border-t-0 border-r-0',
              align === 'center' && (mobilePosition === 'top' || mobilePosition === 'bottom') && 'left-1/2 -translate-x-1/2',
              align === 'left' && (mobilePosition === 'top' || mobilePosition === 'bottom') && 'left-4',
              align === 'right' && (mobilePosition === 'top' || mobilePosition === 'bottom') && 'right-4',
              // If custom className is provided, position arrow in center
              className && 'left-1/2 -translate-x-1/2'
            )}
          />
        </div>
      )}
    </div>
  );
}; 